import React, { useMemo } from 'react';
import { Stack } from '@mui/material';
import { CategoryTO, PageCategoryTO, WordSetCategoryType } from 'app/types';
import CategoryCard from 'components/CategoryCard';

interface ICategoryCardListProps {
  data: PageCategoryTO;
  createLink?: (record: CategoryTO) => string;
  wordSetType?: WordSetCategoryType;
}

/**
 * Parent accent for the catalog row this list belongs to. Must stay in
 * sync with the colours used on the /wordset overview rows — that way
 * the hover/active framing of a CategoryCard reads as the same brand as
 * the /wordset row the user just clicked. CEFR badges (A1/A2/…) keep
 * their own colours via the SVG icon so per-level identity isn't lost.
 *
 * The defaults match WordSetPage's `CURRICULUM_ACCENT` / per-row accent.
 */
const WORDSET_PARENT_ACCENT: Record<WordSetCategoryType, string> = {
  'level-based': '#3B2C93',
  'most-common': '#3B2C93',
  'topic-related': '#3B2C93',
  'phrasal-verbs': '#3B2C93',
  collocations: '#3B2C93',
  idioms: '#3B2C93',
  'irregular-verb': '#3B2C93',
};

const DEFAULT_PARENT_ACCENT = '#3B2C93';

const pickParentAccent = (wordSetType?: WordSetCategoryType): string =>
  (wordSetType && WORDSET_PARENT_ACCENT[wordSetType]) || DEFAULT_PARENT_ACCENT;

const pickActiveCategoryIndex = (categories: CategoryTO[]): number => {
  // The "current" category — the one the user is actively progressing through.
  // Heuristic: most words added but still not all learned. Falls back to any
  // started-but-incomplete row, then -1 when nothing's in progress.
  let bestIdx = -1;
  let bestScore = -1;
  categories.forEach((c, i) => {
    if (c.addedWords <= 0 || c.totalWords <= 0) return;
    if (c.learnedWords >= c.totalWords) return;
    const progress = c.learnedWords / c.totalWords;
    const inProgress = c.addedWords / c.totalWords;
    const score = progress * 1000 + inProgress;
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  });
  return bestIdx;
};

const CategoryCardList: React.FC<ICategoryCardListProps> = ({
  data,
  createLink,
  wordSetType,
}) => {
  const activeIndex = useMemo(
    () => pickActiveCategoryIndex(data.categories),
    [data.categories],
  );

  const parentAccent = pickParentAccent(wordSetType);

  return (
    <Stack spacing={1.5} mb={10}>
      {data.categories.map((record, index) => (
        <CategoryCard
          key={record.categoryName}
          record={record}
          link={createLink?.(record)}
          active={index === activeIndex}
          index={index}
          wordSetType={wordSetType}
          parentAccent={parentAccent}
        />
      ))}
    </Stack>
  );
};

export default CategoryCardList;
